const CURRENCY_EXPONENT = Object.freeze({
  TND: 3,
  EUR: 2,
  USD: 2
});
class MoneyError extends Error {
  name = "MoneyError";
}
const isCurrency = (c) => Object.hasOwn(CURRENCY_EXPONENT, c);
function assertCurrency(c) {
  if (!isCurrency(c)) throw new MoneyError(`unsupported currency ${c}`);
}
function toBigInt(value, what) {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value))
      throw new MoneyError(`${what} must be a safe integer, got ${value}`);
    return BigInt(value);
  }
  if (!/^-?(0|[1-9]\d*)$/.test(value))
    throw new MoneyError(`${what} must be an integer string, got "${value}"`);
  return BigInt(value);
}
function money(amountMinor, currency) {
  assertCurrency(currency);
  return Object.freeze({ amountMinor: toBigInt(amountMinor, "amountMinor"), currency });
}
const zero = (currency) => money(0n, currency);
function assertSameCurrency(a, b) {
  if (a.currency !== b.currency)
    throw new MoneyError(`currency mismatch: ${a.currency} vs ${b.currency}`);
}
function add(a, b) {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}
function sub(a, b) {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}
function sum(items, currency) {
  return items.reduce((acc, m) => add(acc, m), zero(currency));
}
const negate = (a) => money(-a.amountMinor, a.currency);
function divRoundHalfUp(numerator, denominator) {
  if (denominator === 0n) throw new MoneyError("division by zero");
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = n / d;
  const r = n % d;
  const rounded = r * 2n >= d ? q + 1n : q;
  return negative ? -rounded : rounded;
}
function mulRateBp(a, rateBp) {
  return money(divRoundHalfUp(a.amountMinor * toBigInt(rateBp, "rateBp"), 10000n), a.currency);
}
function mulQuantity(unitPrice, quantityMilli) {
  return money(
    divRoundHalfUp(unitPrice.amountMinor * toBigInt(quantityMilli, "quantityMilli"), 1000n),
    unitPrice.currency
  );
}
function allocate(total, ratios) {
  if (ratios.length === 0) throw new MoneyError("allocate needs at least one ratio");
  const weights = ratios.map((r) => toBigInt(r, "ratio"));
  if (weights.some((w) => w < 0n)) throw new MoneyError("ratios must be \u2265 0");
  const totalWeight = weights.reduce((a, b) => a + b, 0n);
  if (totalWeight === 0n) throw new MoneyError("ratios must not all be zero");
  const sign = total.amountMinor < 0n ? -1n : 1n;
  const amount = total.amountMinor * sign;
  const shares = weights.map((w) => amount * w / totalWeight);
  const remainders = weights.map((w, i) => ({ i, r: amount * w % totalWeight }));
  let left = amount - shares.reduce((a, b) => a + b, 0n);
  remainders.sort((x, y) => y.r === x.r ? x.i - y.i : y.r > x.r ? 1 : -1);
  for (const { i } of remainders) {
    if (left === 0n) break;
    shares[i] = shares[i] + 1n;
    left -= 1n;
  }
  return shares.map((s) => money(s * sign, total.currency));
}
function compare(a, b) {
  assertSameCurrency(a, b);
  return a.amountMinor === b.amountMinor ? 0 : a.amountMinor < b.amountMinor ? -1 : 1;
}
const equals = (a, b) => a.currency === b.currency && a.amountMinor === b.amountMinor;
const isZero = (a) => a.amountMinor === 0n;
const isNegative = (a) => a.amountMinor < 0n;
const isPositive = (a) => a.amountMinor > 0n;
const toContract = (a) => ({
  amountMinor: a.amountMinor.toString(),
  currency: a.currency
});
function fromContract(dto) {
  return money(dto.amountMinor, dto.currency);
}
function parseDecimal(text, currency) {
  assertCurrency(currency);
  if (text.length > 64) throw new MoneyError("decimal amount too long");
  const m = /^(-)?(0|[1-9]\d*)(?:\.(\d+))?$/.exec(text.trim());
  if (!m) throw new MoneyError(`not a decimal amount: "${text}"`);
  const minus = m[1];
  const whole = m[2];
  const frac = m[3] ?? "";
  const exp = CURRENCY_EXPONENT[currency];
  if (frac.length > exp)
    throw new MoneyError(`${currency} allows at most ${exp} decimals, got "${text}"`);
  const minor = BigInt(whole) * 10n ** BigInt(exp) + BigInt(frac.padEnd(exp, "0"));
  return money(minus ? -minor : minor, currency);
}
function toDecimalString(a) {
  const exp = CURRENCY_EXPONENT[a.currency];
  const negative = a.amountMinor < 0n;
  const digits = (negative ? -a.amountMinor : a.amountMinor).toString().padStart(exp + 1, "0");
  const whole = digits.slice(0, digits.length - exp);
  const frac = digits.slice(digits.length - exp);
  return `${negative ? "-" : ""}${whole}.${frac}`;
}
export {
  CURRENCY_EXPONENT,
  MoneyError,
  add,
  allocate,
  compare,
  divRoundHalfUp,
  equals,
  fromContract,
  isNegative,
  isPositive,
  isZero,
  money,
  mulQuantity,
  mulRateBp,
  negate,
  parseDecimal,
  sub,
  sum,
  toContract,
  toDecimalString,
  zero
};
//# sourceMappingURL=money.js.map